import {
  LLM_TEMPERATURE,
  ENHANCE_MAX_TOKENS,
  LOG_WORKOUT_MAX_TOKENS,
  LOG_WORKOUT_TOKEN_CEILING,
  TOKEN_ESCALATION_FACTOR,
} from "./config";
import { TruncationError } from "./errors";
import { REPAIR_PROMPT, ENHANCE_PROMPT, LOG_WORKOUT_PROMPT } from "./prompts";
import { ENHANCE_EXERCISE_SCHEMA, LOG_WORKOUT_SCHEMA } from "./schemas";
import { PLAN_REVIEW_SCHEMA, buildPlanReviewPrompt, parsePlanReview } from "./planReview";
import { PROGRAMME_ROWS_SCHEMA, PROGRAMME_ROWS_PROMPT, parseProgrammeRows } from "./programmeImportPrompt";
import { extractJSON, extractAnthropicText } from "./extract";
import { openAIRequestLimits, anthropicMaxTokens } from "./requestLimits";
import { normalizeUnits } from "./postprocess";
import { _dispatchToProvider } from "./providers";
import { getDefaultModel } from "../../config/llmProviders";
import { generateGeminiText, supportsGeminiThinking } from "../geminiApi";
import { cloudRequest, geminiRoute } from "./cloudRoute";
import { extractUsage } from "./usage";
import { trackUsage } from "./usageLog";

const repairSessionCall = async (text, llmSettings, onUsage) => {
  const prompt = REPAIR_PROMPT + text;

  const settings = {
    ...llmSettings,
    apiKey: llmSettings.apiKeys?.[llmSettings.provider] || llmSettings.apiKey,
    // Same reasoning as parseLoggedWorkout: interpreting text someone already
    // wrote is not a reasoning task, and a silent think on a non-streamed
    // request stalls until the proxy cuts the socket.
    geminiThinkingLevel: "OFF",
  };

  const planObject = await _dispatchToProvider(prompt, settings, null, undefined, null, onUsage);

  if (!planObject?.workout_plan) return null;

  const plan = planObject.workout_plan;
  if (!plan.children) return null;

  normalizeUnits(plan);
  return plan;
};

/**
 * Interpret a free-text description of a workout the user has already done
 * (#77) into a plan tree plus session metadata.
 *
 * Same provider dispatch as repairSession, but the result is a RECORD rather
 * than a prescription — see LOG_WORKOUT_PROMPT. The caller previews and edits
 * what comes back before anything is saved, which is the real safeguard: an
 * LLM reading "eight reps six times" can get the sets and reps the wrong way
 * round, and no prompt makes that impossible.
 *
 * @returns {{ plan: object, meta: object }|null} null when nothing usable came
 *   back, so the caller can fall back to the manual form rather than saving a
 *   guess.
 */
const parseLoggedWorkoutCall = async (text, llmSettings, signal, onUsage) => {
  const description = String(text || "").trim();
  if (!description) return null;

  const settings = {
    ...llmSettings,
    apiKey: llmSettings.apiKeys?.[llmSettings.provider] || llmSettings.apiKey,
    // Thinking OFF, deliberately. This is transcription, not planning: there is
    // nothing to reason about in "three sets of 20 burpees", and the reasoning
    // pass is actively harmful here on two counts. It invites the model to
    // improve on what the user said — the one thing this prompt forbids — and,
    // because a non-streamed request shows the proxy no bytes while the model
    // thinks, a long silent reasoning phase trips the frontend proxy's 120s
    // inactivity timeout and the browser reports a bare "Failed to fetch".
    // Streaming is only enabled when a thinking sink is supplied (see
    // `shouldStream` in providers.js), and there is no thinking panel here.
    geminiThinkingLevel: "OFF",
    // The plan AND its `meta` (duration, date, title). With the plain plan
    // schema the schema-enforcing providers silently dropped `meta`, which is
    // why a described workout so rarely came back with a duration.
    responseSchema: LOG_WORKOUT_SCHEMA,
  };

  // Retry on truncation with a doubled budget, the same escalation
  // generateWorkoutPlan uses. A long session ("10 rounds of 10 burpees, 5
  // deadlifts, 20 snatches, then 4 x 400m…") produces a genuinely large tree,
  // and no fixed ceiling is both tight enough to stay fast on a short
  // description and generous enough for a real one — so start low and climb
  // only when the response actually comes back cut off.
  let result;
  let maxTokens = LOG_WORKOUT_MAX_TOKENS;

  for (;;) {
    try {
      result = await _dispatchToProvider(
        LOG_WORKOUT_PROMPT + description,
        settings,
        signal,
        maxTokens,
        null,
        onUsage,
      );
      break;
    } catch (error) {
      if (error?.name === "AbortError") throw error;

      if (error instanceof TruncationError) {
        const next = Math.min(
          maxTokens * TOKEN_ESCALATION_FACTOR,
          LOG_WORKOUT_TOKEN_CEILING,
        );
        if (next > maxTokens) {
          maxTokens = next;
          continue;
        }
        throw new Error(
          "That workout was too long to read in one go. Try splitting it into two entries.",
        );
      }

      // A dropped socket surfaces as a bare TypeError("Failed to fetch") with
      // no status and no body — indistinguishable, to the user, from the app
      // being broken. Name the two things it actually means so the message is
      // actionable rather than merely true.
      if (error instanceof TypeError || /failed to fetch/i.test(error?.message)) {
        throw new Error(
          `Couldn't reach ${llmSettings?.provider || "the model"}. ` +
            "The request was cut off — check your connection and API key, then try again.",
        );
      }
      throw error;
    }
  }

  const plan = result?.workout_plan;
  if (!plan?.children?.length) return null;

  normalizeUnits(plan);
  return { plan, meta: result.meta || {} };
};

const enhanceExerciseCall = async (exerciseName, context, settings, signal, onUsage) => {
  const prompt = ENHANCE_PROMPT(exerciseName, context);
  const apiKey = settings.apiKey || settings.apiKeys?.[settings.provider];

  // Helper for OpenAI-compatible providers (openai, deepseek, groq)
  const openAICompatible = async (path, model) => {
    const route = cloudRequest(settings, path, {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    });
    const res = await fetch(route.url, {
      method: "POST",
      headers: route.headers,
      body: JSON.stringify({
        model: model || settings.model,
        messages: [{ role: "user", content: prompt }],
        ...openAIRequestLimits(settings.provider, ENHANCE_MAX_TOKENS, LLM_TEMPERATURE),
        response_format: { type: "json_object" },
      }),
      signal,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error?.message || `API error: ${res.status}`);
    }
    const data = await res.json();
    onUsage?.(extractUsage(settings.provider, model || settings.model, data));
    return data.choices?.[0]?.message?.content;
  };

  // Helper for Anthropic-compatible providers (anthropic, minimax)
  const anthropicCompatible = async (path, model) => {
    const route = cloudRequest(settings, path, {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      "anthropic-version": "2023-06-01",
    });
    const res = await fetch(route.url, {
      method: "POST",
      headers: route.headers,
      body: JSON.stringify({
        model: model || settings.model,
        max_tokens: anthropicMaxTokens(settings.provider, ENHANCE_MAX_TOKENS),
        messages: [{ role: "user", content: prompt }],
      }),
      signal,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error?.message || `API error: ${res.status}`);
    }
    const data = await res.json();
    onUsage?.(extractUsage(settings.provider, model || settings.model, data));
    return extractAnthropicText(data);
  };

  const makeRequest = async () => {
    switch (settings.provider) {
      case "ollama": {
        const res = await fetch("/ollama-api/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model: settings.model,
            prompt,
            stream: false,
            format: "json", // Added strict JSON mode
          }),
          signal,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `Ollama API error: ${res.status}`);
        }
        const data = await res.json();
        onUsage?.(extractUsage("ollama", settings.model, data));
        return data.response;
      }
      case "openai":
        return openAICompatible(
          "/openai/chat/completions",
          settings.model || getDefaultModel("openai"),
        );
      case "deepseek":
        return openAICompatible("/deepseek/chat/completions");
      case "groq":
        return openAICompatible("/groq/chat/completions");
      case "openrouter":
        return openAICompatible("/openrouter/chat/completions");
      case "anthropic":
        return anthropicCompatible(
          "/anthropic/messages",
          settings.model || getDefaultModel("anthropic"),
        );
      case "minimax":
        return anthropicCompatible("/minimax/v1/messages");
      case "google": {
        const key = apiKey; // using the resolved key from above
        const model = settings.model || getDefaultModel("google");
        const thinkingLevel = settings.geminiThinkingLevel || "HIGH";

        const config = {
          abortSignal: signal,
          responseMimeType: "application/json",
          // Standard JSON Schema — see the note in providers.js; this field is
          // not the uppercase OpenAPI dialect.
          responseJsonSchema: ENHANCE_EXERCISE_SCHEMA,
        };

        if (supportsGeminiThinking(model) && thinkingLevel !== "OFF") {
          config.thinkingConfig = {
            thinkingLevel,
          };
        }

        return generateGeminiText({
          ...geminiRoute(settings, key),
          model,
          contents: prompt,
          config,
          onUsage: (usageMetadata) => onUsage?.(extractUsage("google", model, { usageMetadata })),
        });
      }
      default:
        throw new Error(`Unsupported provider: ${settings.provider}`);
    }
  };

  const raw = await makeRequest();
  if (!raw) {
    console.error(
      "[enhanceExercise] Empty response from provider:",
      settings.provider,
      settings.model,
    );
    throw new Error(
      `No response from ${settings.provider} (${settings.model}). Check your API key and model settings.`,
    );
  }

  return extractJSON(raw);
};

// --- EXERCISE SUBSTITUTION FOR LIVE CO-TRAIN ---

const SUBSTITUTE_MAX_TOKENS = 1024;

/**
 * Call the LLM to generate substitute exercises for equipment mismatches.
 * Follows the same multi-provider dispatch pattern as enhanceExercise.
 *
 * @param {string} prompt    – Built by substituteExercises.buildSubstitutionPrompt()
 * @param {Object} settings  – LLM provider settings (provider, model, apiKey, etc.)
 * @param {AbortSignal} signal – Optional abort signal
 * @returns {Array<{ nodeId, label, equipment, description }>}
 */
const generateExerciseSubstitutionsCall = async (prompt, settings, signal, onUsage) => {
  const apiKey = settings.apiKey || settings.apiKeys?.[settings.provider];

  // Helper for OpenAI-compatible providers
  const openAICompatible = async (path, model) => {
    const route = cloudRequest(settings, path, {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    });
    const res = await fetch(route.url, {
      method: "POST",
      headers: route.headers,
      body: JSON.stringify({
        model: model || settings.model,
        messages: [{ role: "user", content: prompt }],
        // Lower temperature for more reliable substitutions, where accepted.
        ...openAIRequestLimits(settings.provider, SUBSTITUTE_MAX_TOKENS, 0.5),
        response_format: { type: "json_object" },
      }),
      signal,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error?.message || `API error: ${res.status}`);
    }
    const data = await res.json();
    onUsage?.(extractUsage(settings.provider, model || settings.model, data));
    return data.choices?.[0]?.message?.content;
  };

  // Helper for Anthropic-compatible providers
  const anthropicCompatible = async (path, model) => {
    const route = cloudRequest(settings, path, {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      "anthropic-version": "2023-06-01",
    });
    const res = await fetch(route.url, {
      method: "POST",
      headers: route.headers,
      body: JSON.stringify({
        model: model || settings.model,
        max_tokens: anthropicMaxTokens(settings.provider, SUBSTITUTE_MAX_TOKENS),
        messages: [
          {
            role: "user",
            content:
              prompt +
              "\n\nIMPORTANT: Respond with ONLY the JSON array, no other text.",
          },
        ],
      }),
      signal,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error?.message || `API error: ${res.status}`);
    }
    const data = await res.json();
    onUsage?.(extractUsage(settings.provider, model || settings.model, data));
    return extractAnthropicText(data);
  };

  const makeRequest = async () => {
    switch (settings.provider) {
      case "ollama": {
        const res = await fetch("/ollama-api/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model: settings.model,
            prompt,
            stream: false,
            format: "json",
          }),
          signal,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `Ollama API error: ${res.status}`);
        }
        const data = await res.json();
        onUsage?.(extractUsage("ollama", settings.model, data));
        return data.response;
      }
      case "openai":
        return openAICompatible("/openai/chat/completions", settings.model || getDefaultModel("openai"));
      case "deepseek":
        return openAICompatible("/deepseek/chat/completions");
      case "groq":
        return openAICompatible("/groq/chat/completions");
      case "openrouter":
        return openAICompatible("/openrouter/chat/completions");
      case "anthropic":
        return anthropicCompatible("/anthropic/messages", settings.model || getDefaultModel("anthropic"));
      case "minimax":
        return anthropicCompatible("/minimax/v1/messages");
      case "google": {
        const config = {
          abortSignal: signal,
          responseMimeType: "application/json",
          maxOutputTokens: SUBSTITUTE_MAX_TOKENS,
          temperature: 0.5,
        };
        const model = settings.model || getDefaultModel("google");
        return generateGeminiText({
          ...geminiRoute(settings, apiKey),
          model,
          contents: prompt + "\n\nRespond with valid JSON only.",
          config,
          onUsage: (usageMetadata) => onUsage?.(extractUsage("google", model, { usageMetadata })),
        });
      }
      default:
        throw new Error(`Unsupported provider: ${settings.provider}`);
    }
  };

  const raw = await makeRequest();
  if (!raw) {
    throw new Error(
      `No response from ${settings.provider} (${settings.model}). Check your API key and model settings.`
    );
  }

  const parsed = extractJSON(raw);
  // Handle both { substitutions: [...] } and raw array responses
  return Array.isArray(parsed) ? parsed : parsed.substitutions || parsed.exercises || [];
};

// The review lists only a handful of short changes, but Gemini 3 models
// think even with no thinking config, and that counts against the budget: at
// 2048 a review came back cut off. Same budget as a described workout.
const PLAN_REVIEW_MAX_TOKENS = 8192;

/**
 * Notes review of a programme plan (#92): the changes the notes call for, in
 * the fixed edit format. The caller runs them through the planner, which
 * checks each one and leaves out any that break the rules.
 *
 * @param {object} review  buildPlanReviewPrompt's inputs
 * @returns {Promise<object[]>} edits (days from 0), possibly empty
 */
const reviewProgrammePlanCall = async (review, llmSettings, signal, onUsage) => {
  const settings = {
    ...llmSettings,
    apiKey: llmSettings.apiKeys?.[llmSettings.provider] || llmSettings.apiKey,
    // A silent think on a non-streamed request stalls until the proxy cuts the
    // socket (see parseLoggedWorkout); the planner does the checking anyway.
    geminiThinkingLevel: "OFF",
    responseSchema: PLAN_REVIEW_SCHEMA,
  };
  try {
    const result = await _dispatchToProvider(
      buildPlanReviewPrompt(review),
      settings,
      signal,
      PLAN_REVIEW_MAX_TOKENS,
      null,
      onUsage,
    );
    return parsePlanReview(result);
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    if (error instanceof TypeError || /failed to fetch/i.test(error?.message)) {
      throw new Error(
        `Couldn't reach ${llmSettings?.provider || "the model"}. ` +
          "Check your connection and API key, then try again.",
      );
    }
    throw error;
  }
};

/**
 * Read a programme in someone else's layout (a sheet, text copied from a
 * PDF) into import rows (utils/programmeImport.js). Transcription, so
 * thinking is off, as for parseLoggedWorkout; the import's review screen is
 * the check. Retries once with a doubled budget when cut off.
 * @returns {Promise<{ title: string, rows: object[] }>}
 */
const readProgrammeTextCall = async (text, llmSettings, signal, onUsage) => {
  const settings = {
    ...llmSettings,
    apiKey: llmSettings.apiKeys?.[llmSettings.provider] || llmSettings.apiKey,
    geminiThinkingLevel: "OFF",
    responseSchema: PROGRAMME_ROWS_SCHEMA,
  };
  let maxTokens = LOG_WORKOUT_TOKEN_CEILING / 2;
  for (;;) {
    try {
      const result = await _dispatchToProvider(
        PROGRAMME_ROWS_PROMPT + String(text || ""),
        settings,
        signal,
        maxTokens,
        null,
        onUsage,
      );
      return parseProgrammeRows(result);
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      if (error instanceof TruncationError && maxTokens < LOG_WORKOUT_TOKEN_CEILING) {
        maxTokens = LOG_WORKOUT_TOKEN_CEILING;
        continue;
      }
      if (error instanceof TruncationError) {
        throw new Error("That part of the programme was too long to read in one go. Try one week at a time.");
      }
      if (error instanceof TypeError || /failed to fetch/i.test(error?.message)) {
        throw new Error(
          `Couldn't reach ${llmSettings?.provider || "the model"}. ` +
            "Check your connection and API key, then try again.",
        );
      }
      throw error;
    }
  }
};

// Each operation is recorded as one request for the Usage report.
const repairSession = (text, llmSettings) =>
  trackUsage("repair", llmSettings, (onUsage) => repairSessionCall(text, llmSettings, onUsage));
const parseLoggedWorkout = (text, llmSettings, signal = null) =>
  trackUsage("describe", llmSettings, (onUsage) => parseLoggedWorkoutCall(text, llmSettings, signal, onUsage));
const enhanceExercise = (exerciseName, context, settings, signal) =>
  trackUsage("exercise", settings, (onUsage) => enhanceExerciseCall(exerciseName, context, settings, signal, onUsage));
const generateExerciseSubstitutions = (prompt, settings, signal) =>
  trackUsage("substitute", settings, (onUsage) => generateExerciseSubstitutionsCall(prompt, settings, signal, onUsage));
const reviewProgrammePlan = (review, llmSettings, signal = null) =>
  trackUsage("review", llmSettings, (onUsage) => reviewProgrammePlanCall(review, llmSettings, signal, onUsage));
const readProgrammeText = (text, llmSettings, signal = null) =>
  trackUsage("import", llmSettings, (onUsage) => readProgrammeTextCall(text, llmSettings, signal, onUsage));

export {
  readProgrammeText,
  repairSession,
  parseLoggedWorkout,
  enhanceExercise,
  generateExerciseSubstitutions,
  reviewProgrammePlan,
};
